import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Truck, Package, Search, Download, Euro, TrendingUp, Pencil } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IVA_RATES = [
  { value: 23, label: "23% (Normal)" },
  { value: 13, label: "13% (Intermedia)" },
  { value: 6, label: "6% (Reduzida)" },
  { value: 0, label: "0% (Isento)" },
];

export default function SalesGuides() {
  const [guides, setGuides] = useState([]);
  const [parts, setParts] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingGuide, setEditingGuide] = useState(null);
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", nif: "", address: "", city: "", postal_code: "", phone: "", email: "" });
  const [form, setForm] = useState({ client_id: "", client_name: "", client_address: "", client_nif: "", vehicle_plate: "", notes: "", payment_method: "transferencia", payment_terms: "30 dias", iva_rate: 23 });
  const [items, setItems] = useState([]);
  const [searchPart, setSearchPart] = useState("");
  const [showPartsList, setShowPartsList] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [guidesRes, partsRes, stocksRes, clientsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/sales-guides`),
        axios.get(`${API}/parts`),
        axios.get(`${API}/stocks`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/orders`),
      ]);
      setGuides(guidesRes.data);
      setParts(partsRes.data);
      setStocks(stocksRes.data);
      setClients(clientsRes.data);
      setOrders(ordersRes.data.filter(o => o.status === "completed" || o.status === "running" || o.status === "in_progress"));
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getStock = (partId) => {
    const s = stocks.find(st => st.part_id === partId);
    return s ? s.quantity : 0;
  };

  const addItem = (part) => {
    const existing = items.find(i => i.part_id === part.id);
    if (existing) {
      setItems(items.map(i => i.part_id === part.id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setItems([...items, { part_id: part.id, part_code: part.code, part_description: part.description, quantity: 1, unit_price: 0, order_code: "" }]);
    }
    setSearchPart("");
  };

  const updateItem = (idx, field, value) => {
    setItems(items.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const createQuickClient = async () => {
    if (!newClient.name.trim()) { toast.error("Nome obrigatorio"); return; }
    try {
      const res = await axios.post(`${API}/clients`, newClient);
      const created = res.data;
      setClients([...clients, created]);
      setForm({ ...form, client_id: created.id, client_name: created.name, client_nif: created.nif || "", client_address: [created.address, created.city, created.postal_code].filter(Boolean).join(", ") });
      setShowNewClient(false);
      setNewClient({ name: "", nif: "", address: "", city: "", postal_code: "", phone: "", email: "" });
      toast.success("Cliente criado!");
    } catch (e) { showError(e, "Erro ao criar cliente"); }
  };

  const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  const ivaValue = subtotal * (form.iva_rate / 100);
  const totalValue = subtotal + ivaValue;

  const submitGuide = async () => {
    if (!form.client_name.trim()) { toast.error("Nome do cliente obrigatorio"); return; }
    if (items.length === 0) { toast.error("Adicione pelo menos uma peca"); return; }

    try {
      const payload = { 
        ...form, 
        items,
        subtotal: Math.round(subtotal * 100) / 100,
        iva_value: Math.round(ivaValue * 100) / 100,
        total_value: Math.round(totalValue * 100) / 100,
      };

      if (editingGuide) {
        await axios.put(`${API}/sales-guides/${editingGuide.id}`, payload);
        toast.success("Guia atualizada com sucesso!");
      } else {
        await axios.post(`${API}/sales-guides`, payload);
        toast.success("Guia de venda criada com sucesso!");
      }
      setShowCreate(false);
      setEditingGuide(null);
      setForm({ client_id: "", client_name: "", client_address: "", client_nif: "", vehicle_plate: "", notes: "", payment_method: "transferencia", payment_terms: "30 dias", iva_rate: 23 });
      setItems([]);
      fetchData();
    } catch (e) {
      showError(e, "Erro ao guardar guia");
    }
  };

  const openEdit = (guide) => {
    setEditingGuide(guide);
    setForm({
      client_id: guide.client_id || "",
      client_name: guide.client_name || "",
      client_address: guide.client_address || "",
      client_nif: guide.client_nif || "",
      vehicle_plate: guide.vehicle_plate || "",
      notes: guide.notes || "",
      payment_method: guide.payment_method || "transferencia",
      payment_terms: guide.payment_terms || "30 dias",
      iva_rate: guide.iva_rate || 23,
    });
    setItems((guide.items || []).map(item => ({
      part_id: item.part_id,
      part_code: item.part_code,
      part_description: item.part_description,
      quantity: item.quantity,
      unit_price: item.unit_price,
      order_code: item.order_code || "",
    })));
    setShowCreate(true);
  };

  const cancelGuide = async (id) => {
    if (!window.confirm("Tem a certeza que quer anular esta guia? O stock sera reposto.")) return;
    try {
      await axios.delete(`${API}/sales-guides/${id}`);
      toast.success("Guia anulada e stock reposto");
      fetchData();
    } catch (e) { toast.error("Erro ao anular guia"); }
  };

  const exportPDF = (guide) => {
    const win = window.open("", "_blank");
    const itemsHtml = guide.items.map((item, i) => `
      <tr>
        <td style="border:1px solid #ddd;padding:8px;text-align:center;">${i + 1}</td>
        <td style="border:1px solid #ddd;padding:8px;font-weight:600;">${item.part_code}</td>
        <td style="border:1px solid #ddd;padding:8px;">${item.part_description}</td>
        <td style="border:1px solid #ddd;padding:8px;text-align:center;">${item.order_code || "-"}</td>
        <td style="border:1px solid #ddd;padding:8px;text-align:center;">${item.quantity}</td>
        <td style="border:1px solid #ddd;padding:8px;text-align:right;">${item.unit_price.toFixed(4)} &euro;</td>
        <td style="border:1px solid #ddd;padding:8px;text-align:right;font-weight:bold;">${item.total.toFixed(2)} &euro;</td>
      </tr>`).join("");
    
    const subtotalVal = guide.subtotal || guide.total_value || 0;
    const ivaRateVal = guide.iva_rate || 23;
    const ivaVal = guide.iva_value || (subtotalVal * ivaRateVal / 100);
    const totalVal = guide.total_value_with_iva || (subtotalVal + ivaVal);

    win.document.write(`<!DOCTYPE html><html><head><title>${guide.number}</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;padding:30px;max-width:800px;margin:0 auto;font-size:13px}
        .header{display:flex;justify-content:space-between;align-items:start;border-bottom:3px solid #1a365d;padding-bottom:15px;margin-bottom:20px}
        .company{font-size:11px;color:#333;line-height:1.6}
        .company strong{font-size:14px;color:#1a365d}
        .doc-info{text-align:right}
        .doc-info h1{font-size:20px;color:#1a365d;margin:0}
        .doc-info .number{font-size:16px;font-weight:bold;color:#333;margin:4px 0}
        .doc-info .date{color:#666;font-size:12px}
        .parties{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:25px}
        .party{background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:12px}
        .party h3{font-size:11px;text-transform:uppercase;color:#64748b;margin:0 0 6px 0;letter-spacing:0.5px}
        .party p{margin:2px 0;font-size:13px}
        .party .name{font-weight:700;font-size:14px;color:#1a365d}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#1a365d;color:white;padding:8px 10px;text-align:left;font-size:11px;text-transform:uppercase}
        td{font-size:12px}
        .totals{float:right;width:280px;margin-top:10px}
        .totals table{margin:0}.totals td{padding:6px 10px;border:none}
        .totals .label{text-align:right;color:#64748b;font-weight:500}
        .totals .value{text-align:right;font-weight:600}
        .totals .grand{background:#f0f9ff;font-size:16px;font-weight:700;color:#1a365d}
        .conditions{clear:both;margin-top:30px;padding-top:15px;border-top:1px solid #e2e8f0;font-size:11px;color:#666}
        .conditions .grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:15px}
        .conditions strong{color:#333}
        .footer-sigs{margin-top:50px;display:grid;grid-template-columns:1fr 1fr;gap:60px}
        .sig-line{border-top:1px solid #333;margin-top:50px;padding-top:5px;text-align:center;font-size:11px;color:#666}
        .legal{margin-top:30px;text-align:center;font-size:10px;color:#999;border-top:1px solid #eee;padding-top:10px}
        @media print{body{padding:15px}}
      </style></head><body>
      <div class="header">
        <div class="company">
          <img src="/ep3d-logo.png" style="height:45px;margin-bottom:5px" onerror="this.style.display='none'"><br>
          <strong>EP3D - ENGINEERING PROJECT 3D</strong><br>
          Zona Industrial de Santarem<br>
          NIF: 517 316 188<br>
          Tel: 243 XXX XXX | geral@engenharia3d.pt
        </div>
        <div class="doc-info">
          <h1>GUIA DE VENDA</h1>
          <div class="number">${guide.number}</div>
          <div class="date">${new Date(guide.created_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" })}</div>
        </div>
      </div>
      <div class="parties">
        <div class="party">
          <h3>Dados do Cliente</h3>
          <p class="name">${guide.client_name}</p>
          ${guide.client_nif ? `<p><strong>NIF:</strong> ${guide.client_nif}</p>` : ""}
          ${guide.client_address ? `<p>${guide.client_address}</p>` : ""}
        </div>
        <div class="party">
          <h3>Dados do Documento</h3>
          <p><strong>Documento:</strong> ${guide.number}</p>
          <p><strong>Data:</strong> ${new Date(guide.created_at).toLocaleDateString("pt-PT")}</p>
          <p><strong>Emitido por:</strong> ${guide.created_by_name || guide.created_by}</p>
          ${guide.vehicle_plate ? `<p><strong>Matricula:</strong> ${guide.vehicle_plate}</p>` : ""}
          ${guide.payment_method ? `<p><strong>Pagamento:</strong> ${guide.payment_method}</p>` : ""}
        </div>
      </div>
      <table>
        <thead><tr><th>#</th><th>Ref.</th><th>Descricao</th><th style="text-align:center">OF</th><th style="text-align:center">Qtd.</th><th style="text-align:right">Preco Unit.</th><th style="text-align:right">Total</th></tr></thead>
        <tbody>${itemsHtml}</tbody>
      </table>
      <div class="totals">
        <table>
          <tr><td class="label">Subtotal:</td><td class="value">${subtotalVal.toFixed(2)} &euro;</td></tr>
          <tr><td class="label">IVA (${ivaRateVal}%):</td><td class="value">${ivaVal.toFixed(2)} &euro;</td></tr>
          <tr class="grand"><td class="label">TOTAL:</td><td class="value">${totalVal.toFixed(2)} &euro;</td></tr>
        </table>
      </div>
      <div class="conditions">
        <div class="grid">
          <div><strong>Cond. Pagamento:</strong><br>${guide.payment_terms || "30 dias"}</div>
          <div><strong>Forma Pagamento:</strong><br>${guide.payment_method || "Transferencia bancaria"}</div>
          <div><strong>Observacoes:</strong><br>${guide.notes || "-"}</div>
        </div>
      </div>
      <div class="footer-sigs">
        <div><div class="sig-line">O Vendedor</div></div>
        <div><div class="sig-line">Recebi / O Cliente</div></div>
      </div>
      <div class="legal">Documento emitido por software EP3D MES</div>
    </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  const filteredParts = searchPart.length > 0
    ? parts.filter(p => p.code.toLowerCase().includes(searchPart.toLowerCase()) || p.description.toLowerCase().includes(searchPart.toLowerCase()))
    : (showPartsList ? parts : []);

  // KPIs
  const totalSales = guides.filter(g => g.status === "emitida").reduce((sum, g) => sum + (g.total_value || 0), 0);
  const guidesThisMonth = guides.filter(g => {
    if (!g.created_at) return false;
    const d = new Date(g.created_at);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear() && g.status === "emitida";
  });
  const monthSales = guidesThisMonth.reduce((sum, g) => sum + (g.total_value || 0), 0);

  return (
    <div className="space-y-6" data-testid="sales-guides-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Guias de Venda</h1>
          <p className="text-gray-500 mt-1">Emissao de guias e saida de stock</p>
        </div>
        <Button onClick={() => { setEditingGuide(null); setForm({ client_id: "", client_name: "", client_address: "", client_nif: "", vehicle_plate: "", notes: "", payment_method: "transferencia", payment_terms: "30 dias", iva_rate: 23 }); setItems([]); setShowCreate(true); }} data-testid="new-guide-btn">
          <Plus className="h-4 w-4 mr-2" /> Nova Guia de Venda
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 uppercase">Total Guias</p><p className="text-2xl font-bold text-blue-600">{guides.length}</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 uppercase">Vendas Totais</p><p className="text-2xl font-bold text-green-600">{totalSales.toFixed(2)} &euro;</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 uppercase">Este Mes</p><p className="text-2xl font-bold text-purple-600">{monthSales.toFixed(2)} &euro;</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 uppercase">Guias Anuladas</p><p className="text-2xl font-bold text-red-600">{guides.filter(g => g.status === "anulada").length}</p></CardContent></Card>
      </div>

      {/* Lista de guias */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" /> Guias Emitidas ({guides.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numero</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>NIF</TableHead>
                <TableHead>Pecas</TableHead>
                <TableHead>Subtotal</TableHead>
                <TableHead>IVA</TableHead>
                <TableHead>Total c/ IVA</TableHead>
                <TableHead>Matricula</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {guides.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center text-gray-400 py-8">Nenhuma guia emitida</TableCell></TableRow>
              ) : guides.map(g => {
                const sub = g.subtotal || g.total_value || 0;
                const iva = g.iva_value || 0;
                const tot = g.total_value_with_iva || g.total_value || 0;
                return (
                  <TableRow key={g.id}>
                    <TableCell className="font-mono font-bold">{g.number}</TableCell>
                    <TableCell className="font-medium">{g.client_name}</TableCell>
                    <TableCell>{g.client_nif || "-"}</TableCell>
                    <TableCell>{g.items?.length || 0} ref.</TableCell>
                    <TableCell>{sub.toFixed(2)} &euro;</TableCell>
                    <TableCell>{iva.toFixed(2)} &euro;</TableCell>
                    <TableCell className="font-bold">{tot.toFixed(2)} &euro;</TableCell>
                    <TableCell>{g.vehicle_plate || "-"}</TableCell>
                    <TableCell>{g.created_at ? new Date(g.created_at).toLocaleDateString("pt-PT") : "-"}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${g.status === 'emitida' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {g.status === 'emitida' ? 'Emitida' : 'Anulada'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => exportPDF(g)} className="text-blue-600" title="Exportar PDF">
                          <Download className="h-4 w-4" />
                        </Button>
                        {g.status === 'emitida' && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => openEdit(g)} className="text-gray-600" title="Editar">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => cancelGuide(g.id)} className="text-red-500 hover:text-red-700" title="Anular">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog criar guia */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" /> {editingGuide ? `Editar Guia ${editingGuide.number}` : "Nova Guia de Venda"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            {/* Selecao de cliente */}
            <div>
              <label className="text-sm font-semibold text-gray-700">Cliente *</label>
              <div className="flex gap-2 mt-1">
                <select className="flex-1 border rounded-lg px-3 py-2 text-sm" value={form.client_id} onChange={e => {
                  const client = clients.find(c => c.id === e.target.value);
                  if (client) {
                    setForm({ ...form, client_id: client.id, client_name: client.name, client_nif: client.nif || "", client_address: [client.address, client.city, client.postal_code].filter(Boolean).join(", ") });
                  } else {
                    setForm({ ...form, client_id: "", client_name: "", client_nif: "", client_address: "" });
                  }
                }} data-testid="guide-client-select">
                  <option value="">-- Selecione um cliente --</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name} {c.nif ? `(NIF: ${c.nif})` : ""}</option>
                  ))}
                </select>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowNewClient(true)} className="whitespace-nowrap"><Plus className="h-3.5 w-3.5 mr-1" />Novo Cliente</Button>
              </div>
            </div>

            {/* Dados do cliente */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700">Nome do Cliente</label>
                <input className="w-full mt-1 border rounded-lg px-3 py-2 text-sm bg-gray-50" value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} placeholder="Preenchido automaticamente" data-testid="guide-client-name" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">NIF</label>
                <input className="w-full mt-1 border rounded-lg px-3 py-2 text-sm bg-gray-50" value={form.client_nif} onChange={e => setForm({ ...form, client_nif: e.target.value })} placeholder="Contribuinte" data-testid="guide-client-nif" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Morada</label>
                <input className="w-full mt-1 border rounded-lg px-3 py-2 text-sm bg-gray-50" value={form.client_address} onChange={e => setForm({ ...form, client_address: e.target.value })} placeholder="Morada" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-1"><Truck className="h-3.5 w-3.5" /> Matricula (opcional)</label>
                <input className="w-full mt-1 border rounded-lg px-3 py-2 text-sm" value={form.vehicle_plate} onChange={e => setForm({ ...form, vehicle_plate: e.target.value })} placeholder="Ex: 00-AA-00" data-testid="guide-vehicle" />
              </div>
            </div>

            {/* Condicoes */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700">Forma de Pagamento</label>
                <select className="w-full mt-1 border rounded-lg px-3 py-2 text-sm" value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}>
                  <option value="transferencia">Transferencia Bancaria</option>
                  <option value="dinheiro">Dinheiro</option>
                  <option value="cheque">Cheque</option>
                  <option value="mbway">MB Way</option>
                  <option value="multibanco">Multibanco</option>
                  <option value="credito">A credito</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Condicoes Pagamento</label>
                <select className="w-full mt-1 border rounded-lg px-3 py-2 text-sm" value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })}>
                  <option value="pronto pagamento">Pronto Pagamento</option>
                  <option value="15 dias">15 dias</option>
                  <option value="30 dias">30 dias</option>
                  <option value="60 dias">60 dias</option>
                  <option value="90 dias">90 dias</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Taxa IVA</label>
                <select className="w-full mt-1 border rounded-lg px-3 py-2 text-sm" value={form.iva_rate} onChange={e => setForm({ ...form, iva_rate: parseInt(e.target.value) })}>
                  {IVA_RATES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>

            {/* Adicionar pecas */}
            <div>
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-1"><Package className="h-3.5 w-3.5" /> Pecas / Artigos</label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm" value={searchPart} onChange={e => setSearchPart(e.target.value)} onFocus={() => setShowPartsList(true)} onBlur={() => setTimeout(() => setShowPartsList(false), 200)} placeholder="Clique para ver todos ou pesquise..." data-testid="guide-search-part" />
                {filteredParts.length > 0 && (
                  <div className="absolute z-10 w-full bg-white border rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                    {filteredParts.map(p => (
                      <button key={p.id} onClick={() => addItem(p)} className="w-full text-left px-4 py-2 hover:bg-blue-50 flex items-center justify-between text-sm">
                        <span><strong>{p.code}</strong> - {p.description}</span>
                        <span className="text-gray-400">Stock: {getStock(p.id)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Tabela de items */}
            {items.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ref.</TableHead>
                    <TableHead>Descricao</TableHead>
                    <TableHead>OF</TableHead>
                    <TableHead>Stock</TableHead>
                    <TableHead>Qtd</TableHead>
                    <TableHead>Preco Unit.</TableHead>
                    <TableHead>Total Linha</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono font-bold">{item.part_code}</TableCell>
                      <TableCell>{item.part_description}</TableCell>
                      <TableCell>
                        <select className="w-28 border rounded px-1 py-1 text-xs" value={item.order_code || ""} onChange={e => updateItem(idx, "order_code", e.target.value)}>
                          <option value="">-</option>
                          {orders.filter(o => o.part_id === item.part_id || !item.part_id).map(o => (
                            <option key={o.id} value={o.code}>{o.code}</option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell className="text-gray-500">{getStock(item.part_id)}</TableCell>
                      <TableCell><input type="number" min="1" className="w-20 border rounded px-2 py-1 text-sm" value={item.quantity} onChange={e => updateItem(idx, "quantity", parseInt(e.target.value) || 1)} /></TableCell>
                      <TableCell><input type="number" min="0" step="0.001" className="w-24 border rounded px-2 py-1 text-sm" value={item.unit_price} onChange={e => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)} placeholder="0.00" /></TableCell>
                      <TableCell className="font-bold">{(item.quantity * item.unit_price).toFixed(2)} &euro;</TableCell>
                      <TableCell><Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* Totais */}
            {items.length > 0 && (
              <div className="flex justify-end">
                <div className="w-72 space-y-1 bg-slate-50 rounded-lg p-4 border">
                  <div className="flex justify-between text-sm"><span className="text-gray-600">Subtotal:</span><span className="font-medium">{subtotal.toFixed(2)} &euro;</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-600">IVA ({form.iva_rate}%):</span><span className="font-medium">{ivaValue.toFixed(2)} &euro;</span></div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2"><span>TOTAL:</span><span className="text-green-700">{totalValue.toFixed(2)} &euro;</span></div>
                </div>
              </div>
            )}

            {/* Observacoes */}
            <div>
              <label className="text-sm font-semibold text-gray-700">Observacoes</label>
              <textarea className="w-full mt-1 border rounded-lg px-3 py-2 text-sm" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notas adicionais..." />
            </div>

            {/* Botoes */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button onClick={submitGuide} data-testid="submit-guide-btn" className="bg-green-600 hover:bg-green-700">
                <FileText className="h-4 w-4 mr-2" /> {editingGuide ? "Guardar Alteracoes" : "Emitir Guia de Venda"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog criar cliente rapido */}
      <Dialog open={showNewClient} onOpenChange={setShowNewClient}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Novo Cliente Rapido</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="col-span-2"><label className="text-xs font-semibold text-gray-600">Nome *</label><input className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={newClient.name} onChange={e => setNewClient({...newClient, name: e.target.value})} placeholder="Nome do cliente" /></div>
            <div><label className="text-xs font-semibold text-gray-600">NIF</label><input className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={newClient.nif} onChange={e => setNewClient({...newClient, nif: e.target.value})} placeholder="Contribuinte" /></div>
            <div><label className="text-xs font-semibold text-gray-600">Telefone</label><input className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={newClient.phone} onChange={e => setNewClient({...newClient, phone: e.target.value})} placeholder="Telefone" /></div>
            <div className="col-span-2"><label className="text-xs font-semibold text-gray-600">Morada</label><input className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={newClient.address} onChange={e => setNewClient({...newClient, address: e.target.value})} placeholder="Morada" /></div>
            <div><label className="text-xs font-semibold text-gray-600">Cidade</label><input className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={newClient.city} onChange={e => setNewClient({...newClient, city: e.target.value})} placeholder="Cidade" /></div>
            <div><label className="text-xs font-semibold text-gray-600">Cod. Postal</label><input className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={newClient.postal_code} onChange={e => setNewClient({...newClient, postal_code: e.target.value})} placeholder="0000-000" /></div>
            <div className="col-span-2"><label className="text-xs font-semibold text-gray-600">Email</label><input className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={newClient.email} onChange={e => setNewClient({...newClient, email: e.target.value})} placeholder="email@empresa.pt" /></div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowNewClient(false)}>Cancelar</Button>
            <Button onClick={createQuickClient}>Criar Cliente</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
